import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Download, ShieldCheck, Heart, ChevronRight, Info, BellRing } from "lucide-react";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import ProductVial from "@/components/ProductVial";
import ProductCard from "@/components/product/ProductCard";
import LabMetricBadge from "@/components/LabMetricBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { getProductImage } from "@/constants/productImages";
import { QuoteRequestDialog } from "@/components/product/QuoteRequestDialog";
import { applyPageMeta, useProductJsonLd } from "@/lib/seo";
import { toast } from "sonner";

function stockBadge(qty) {
  if (qty === 0) return { cls: "bg-red-50 text-red-700", label: "Out of Stock" };
  if (qty <= 5) return { cls: "bg-amber-50 text-amber-700", label: "Low Stock" };
  return { cls: "bg-emerald-50 text-emerald-700", label: "In Stock" };
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [category, setCategory] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertSubmitting, setAlertSubmitting] = useState(false);

  const submitRestockAlert = async () => {
    if (!alertEmail.trim()) return toast.error("Please enter your email address");
    setAlertSubmitting(true);
    try {
      const { data } = await apiClient.post(`/products/${product.id}/restock-alert`, { email: alertEmail.trim() });
      toast.success(data.message);
      setAlertEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save your alert. Please try again.");
    } finally {
      setAlertSubmitting(false);
    }
  };

  useEffect(() => {
    setNotFound(false);
    apiClient.get(`/products/${slug}`)
      .then(async (res) => {
        setProduct(res.data);
        const catRes = await apiClient.get("/categories");
        setCategory(catRes.data.find((c) => c.id === res.data.category_id));
      })
      .catch(() => setNotFound(true));
    apiClient.get(`/products/${slug}/related`).then((res) => setRelated(res.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    document.title = product.seo_title || `${product.name} ${product.strength} | Research Use Only | True Impact Peptides`;
    applyPageMeta({
      title: null,
      description: product.seo_description || `${product.name} ${product.strength} Research Use Only compound from True Impact Peptides. Independently tested with batch-specific Certificate of Analysis.`,
      path: `/product/${product.slug}`,
    });
    document.title = product.seo_title || `${product.name} ${product.strength} | Research Use Only | True Impact Peptides`;
  }, [product]);

  useProductJsonLd(product);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="product-not-found">
        <h1 className="text-2xl font-display font-semibold text-slate-900">Product Not Found</h1>
        <Link to="/shop" className="text-brandblue-600 text-sm font-semibold mt-4 inline-block">Back to Shop</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="max-w-7xl mx-auto px-6 py-24" data-testid="product-loading"><div className="h-96 bg-slate-100 rounded-2xl animate-pulse" /></div>;
  }

  const realImage = getProductImage(product.slug, product);

  const handleAddToCart = () => {
    addItem(product, quantity);
    toast.success(`${quantity} x ${product.name} added to cart`);
  };

  const handleWishlist = async () => {
    if (!user) return navigate("/login");
    try {
      await apiClient.post(`/account/wishlist/${product.id}`);
      toast.success("Added to wishlist");
    } catch {
      toast.error("Could not add to wishlist");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12" data-testid="product-detail-page">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-8">
        <Link to="/shop">Shop</Link><ChevronRight className="h-3 w-3" />
        <span className="text-slate-900">{product.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-14">
        <div className={`relative ${realImage ? "bg-white border border-slate-100" : "bg-gradient-to-b from-blue-50/70 to-slate-50"} rounded-3xl flex items-center justify-center p-10 h-[420px] overflow-hidden shadow-premium-sm`}>
          <div className="absolute inset-0 bg-radial-blue-glow opacity-80" />
          {realImage ? (
            <img src={realImage} alt={`${product.name} ${product.strength} research peptide vial — Research Use Only`} className="h-full w-full object-contain relative z-10" data-testid="product-detail-image" />
          ) : (
            <ProductVial categorySlug={category?.slug} name={product.name} strength={product.strength} className="h-80 relative z-10" />
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-brandblue-600 font-semibold">{category?.name}</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-display font-medium text-slate-900" data-testid="product-detail-name">{product.name}</h1>
          <p className="mt-1 text-slate-500">{product.strength} &middot; SKU: {product.sku}</p>

          <div className="mt-5 flex items-center gap-4">
            <span className="text-3xl font-display font-bold text-slate-900" data-testid="product-detail-price">${product.price.toFixed(2)}</span>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${stockBadge(product.stock_quantity).cls}`} data-testid="product-detail-availability">
              {stockBadge(product.stock_quantity).label}
            </span>
          </div>

          <p className="mt-6 text-sm text-slate-600 leading-relaxed">{product.description}</p>

          <div className="mt-6 grid grid-cols-2 gap-3" data-testid="product-coa-summary">
            <LabMetricBadge label="HPLC Tested" value="Verified" verified />
            <LabMetricBadge label="Batch Status" value="Verified" verified />
            <LabMetricBadge label="Purity" value="99%+" verified />
            <LabMetricBadge label="Third-Party Tested" value="Verified" verified />
          </div>
          <p className="mt-3 text-xs text-slate-500">Batch <span className="font-semibold text-slate-700">{product.coa.batch_number}</span> &middot; Manufactured {product.coa.manufacturing_date} &middot; Tested {product.coa.test_date}</p>

          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50/60 p-4" data-testid="product-ruo-notice">
            <Info className="h-4 w-4 text-brandblue-600 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-900">Research Use Only.</span> This product is intended exclusively for lawful laboratory research purposes. Not for human consumption. Please review our{" "}
              <Link to="/research-use-disclaimer" target="_blank" rel="noopener noreferrer" className="text-brandblue-600 font-semibold hover:text-brandblue-800 transition-colors" data-testid="product-ruo-disclaimer-link">
                Research Use Only Disclaimer
              </Link>{" "}
              before purchasing.
            </p>
          </div>

          <p className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2" data-testid="product-qty-discount-message">
            Buy 3 or more of the same vial and automatically save 10%.
          </p>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-slate-200 rounded-full">
              <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="px-4 py-2.5 text-slate-600 hover:text-brandblue-600 transition-colors" data-testid="quantity-decrease-btn">-</button>
              <span className="px-3 text-sm font-semibold" data-testid="quantity-value">{quantity}</span>
              <button onClick={() => setQuantity((q) => Math.min(product.stock_quantity, q + 1))} className="px-4 py-2.5 text-slate-600 hover:text-brandblue-600 transition-colors" data-testid="quantity-increase-btn">+</button>
            </div>
            <Button size="lg" className="flex-1 rounded-full btn-lift" onClick={handleAddToCart} disabled={product.stock_quantity === 0} data-testid="product-detail-add-to-cart-btn">
              <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
            </Button>
            <button onClick={handleWishlist} className="p-3 border border-slate-200 rounded-full hover:bg-blue-50 hover:border-blue-200 hover:shadow-blue-glow-sm transition-all duration-300" data-testid="add-to-wishlist-btn">
              <Heart className="h-5 w-5 text-slate-600 hover:text-brandblue-600" />
            </button>
          </div>

          <div className="mt-3">
            <QuoteRequestDialog product={product} />
            <p className="text-xs text-slate-500 mt-1.5 text-center">Ordering for a laboratory or institution? Get bulk pricing on this compound.</p>
          </div>

          {product.stock_quantity === 0 && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="restock-alert-box">
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <BellRing className="h-4 w-4 text-amber-600 shrink-0" /> Out of stock — get notified
              </p>
              <p className="text-xs text-slate-500 leading-relaxed mt-1">Leave your email and we'll let you know the moment this vial is back in stock.</p>
              <div className="flex gap-2 mt-3">
                <Input
                  type="email"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-white"
                  data-testid="restock-email-input"
                />
                <Button size="sm" className="rounded-full px-5 shrink-0 h-10" onClick={submitRestockAlert} disabled={alertSubmitting} data-testid="restock-notify-btn">
                  {alertSubmitting ? "Saving..." : "Notify Me"}
                </Button>
              </div>
            </div>
          )}

          {/* HIDDEN: uncomment to restore link to COA Library page
          <Link to={`/coa-library`} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brandblue-600 hover:text-brandblue-800 transition-colors" data-testid="product-view-coa-link">
            <Download className="h-4 w-4" /> View Certificate of Analysis
          </Link>
          */}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-10 mt-16 pt-16 border-t border-slate-200">
        <div>
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brandblue-600" /> Research Information</h2>
          <p className="text-sm text-slate-600 leading-relaxed">{product.research_info}</p>
        </div>
        <div>
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">Storage Information</h2>
          <p className="text-sm text-slate-600 leading-relaxed">{product.storage_information}</p>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-20">
          <h2 className="text-2xl font-display font-medium text-slate-900 mb-8">Related Research Compounds</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p) => <ProductCard key={p.slug} product={p} categorySlug={category?.slug} />)}
          </div>
        </div>
      )}
    </div>
  );
}
